/* best-sellers.js (OPTIMIZED v2 — copy/paste)
  ✅ Changes vs v1
  - Nav disabled uses SAME maxV as progress (consistent end behavior)
  - Cache cards + maxV; recompute only on resize/tab swap/content change
  - Prev/Next index uses offsetLeft (no getBoundingClientRect loops)
  - Section load: robust root detection
  - Remove reliance on CSS scroll-behavior (JS controls smooth)
  - Cleanup kept (section unload)
*/
(function () {
  if (window.__psBestSellersScriptLoaded) return;
  window.__psBestSellersScriptLoaded = true;

  const qs = (root, sel) => (root ? root.querySelector(sel) : null);
  const qsa = (root, sel) => (root ? Array.from(root.querySelectorAll(sel)) : []);
  const num = (v) => {
    const n = parseFloat(v || '0');
    return Number.isFinite(n) ? n : 0;
  };
  const clamp = (n, a, b) => (n < a ? a : n > b ? b : n);

  const INITTED = new WeakMap();

  /* -----------------------------
   * Small rAF scheduler (per handler)
   * ----------------------------- */
  function rafOnce(fn) {
    let id = 0;
    return function () {
      if (id) return;
      id = requestAnimationFrame(() => {
        id = 0;
        fn();
      });
    };
  }

  /* -----------------------------
   * Panel controller: Progress + Drag + Nav
   * ----------------------------- */
  function setupPanel(panel) {
    const scroller = qs(panel, '[data-ps-scroll]');
    const track = qs(panel, '[data-ps-progress-track]');
    const bar = qs(panel, '[data-ps-progress-bar]');
    const rail = qs(panel, '[data-ps-rail]');
    const btnPrev = rail ? qs(rail, '[data-ps-nav="prev"]') : null;
    const btnNext = rail ? qs(rail, '[data-ps-nav="next"]') : null;
    const grid = qs(panel, '.ps__grid--scroll') || qs(panel, '.ps__grid');

    if (!scroller || !grid) return null;

    const hasProgress = !!(track && bar);
    const hasNav = !!(btnPrev && btnNext);

    /* ---- Cached measurements ---- */
    let cards = [];
    let cardsReady = false;

    let cachedMaxV = 0;
    let maxReady = false;

    const readSP = (side) => {
      const cs = getComputedStyle(scroller);
      const sp = side === 'l' ? cs.scrollPaddingLeft : cs.scrollPaddingRight;
      const p = side === 'l' ? cs.paddingLeft : cs.paddingRight;
      return num(sp) || num(p);
    };

    const refreshCards = () => {
      // include only .ps-card (end spacer excluded)
      cards = Array.from(grid.querySelectorAll('.ps-card'));
      cardsReady = true;
      maxReady = false; // last card edge may change
    };

    const computeMaxV = () => {
      if (!cardsReady) refreshCards();

      const viewW = scroller.clientWidth;
      const maxNative = Math.max(0, scroller.scrollWidth - viewW);
      if (!maxNative) {
        cachedMaxV = 0;
        maxReady = true;
        return cachedMaxV;
      }

      const pr = readSP('r');
      const lastCard = cards.length ? cards[cards.length - 1] : null;

      if (lastCard) {
        // Use offsetLeft/offsetWidth (cheap; no layout thrash loops)
        const lastRight = lastCard.offsetLeft + lastCard.offsetWidth;
        const maxToLastEdge = Math.max(0, lastRight - (viewW - pr));
        cachedMaxV = Math.min(maxNative, maxToLastEdge);
      } else {
        cachedMaxV = maxNative;
      }

      maxReady = true;
      return cachedMaxV;
    };

    const getMaxV = () => (maxReady ? cachedMaxV : computeMaxV());

    /* ---- Progress thumb sizing + position ---- */
    const updateProgress = () => {
      if (!hasProgress) return;

      const viewW = scroller.clientWidth;
      const maxV = getMaxV();
      const trackW = track.clientWidth;

      const effectiveScrollW = viewW + maxV;
      let ratio = effectiveScrollW > 0 ? viewW / effectiveScrollW : 1;
      ratio = clamp(ratio, 0.18, 1);

      const barW = Math.round(trackW * ratio);
      const travel = Math.max(0, trackW - barW);

      const p = maxV > 0 ? clamp(scroller.scrollLeft / maxV, 0, 1) : 0;
      const x = Math.round(travel * p);

      bar.style.width = barW + 'px';
      bar.style.transform = 'translateX(' + x + 'px)';
    };

    /* ---- Nav disabled state (✅ same maxV as progress) ---- */
    const updateNavDisabled = () => {
      if (!hasNav) return;

      const maxV = getMaxV();
      const left = scroller.scrollLeft;

      const atStart = left <= 1;
      const atEnd = left >= maxV - 1;

      btnPrev.classList.toggle('is-disabled', atStart);
      btnNext.classList.toggle('is-disabled', atEnd);
      btnPrev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      btnNext.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    };

    const syncUI = () => {
      updateProgress();
      updateNavDisabled();
    };

    const onScroll = rafOnce(syncUI);
    scroller.addEventListener('scroll', onScroll, { passive: true });

    /* ---- Prev/Next: always move to adjacent CARD ---- */
    const currentBaseLeft = () => scroller.scrollLeft + readSP('l');

    const findNextIndex = () => {
      if (!cardsReady) refreshCards();

      const base = currentBaseLeft();
      for (let i = 0; i < cards.length; i++) {
        if (cards[i].offsetLeft > base + 1) return i;
      }
      return Math.max(0, cards.length - 1);
    };

    const findPrevIndex = () => {
      if (!cardsReady) refreshCards();

      const base = currentBaseLeft();
      for (let i = cards.length - 1; i >= 0; i--) {
        if (cards[i].offsetLeft < base - 1) return i;
      }
      return 0;
    };

    const scrollToCard = (idx) => {
      if (!cardsReady) refreshCards();
      if (!cards.length) return;

      const i = clamp(idx, 0, cards.length - 1);
      const target = cards[i];

      // Prefer native snap align
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      } catch (e) {
        // fallback
        const left = Math.max(0, target.offsetLeft - readSP('l'));
        scroller.scrollTo({ left, behavior: 'smooth' });
      }
    };

    const onPrev = () => scrollToCard(findPrevIndex());
    const onNext = () => scrollToCard(findNextIndex());

    if (hasNav) {
      btnPrev.addEventListener('click', onPrev);
      btnNext.addEventListener('click', onNext);
    }

    /* ---- Drag seek on progress track (smooth rAF) ---- */
    let dragging = false;
    let pid = null;
    let rect = null;

    let dragMaxV = 0;
    let dragBarW = 0;
    let dragTravel = 1;

    let pendingX = null;
    let dragRaf = 0;

    const computeDragMetrics = () => {
      if (!hasProgress) return;

      // ensure caches valid
      if (!cardsReady) refreshCards();
      dragMaxV = getMaxV();

      rect = track.getBoundingClientRect();

      const viewW = scroller.clientWidth;
      const trackW = rect.width;

      const effectiveScrollW = viewW + dragMaxV;
      let ratio = effectiveScrollW > 0 ? viewW / effectiveScrollW : 1;
      ratio = clamp(ratio, 0.18, 1);

      dragBarW = Math.round(trackW * ratio);
      dragTravel = Math.max(1, trackW - dragBarW);
    };

    const applySeek = () => {
      dragRaf = 0;
      if (!rect || pendingX == null) return;

      const x = pendingX - rect.left;
      const p = clamp((x - dragBarW / 2) / dragTravel, 0, 1);

      scroller.scrollLeft = dragMaxV * p;
      // keep UI in sync immediately
      syncUI();
    };

    const scheduleSeek = (clientX) => {
      pendingX = clientX;
      if (dragRaf) return;
      dragRaf = requestAnimationFrame(applySeek);
    };

    const endDrag = (e) => {
      if (!dragging) return;
      if (pid != null && e.pointerId !== pid) return;

      dragging = false;
      try {
        track.releasePointerCapture(pid);
      } catch (err) {}
      pid = null;

      pendingX = null;
      if (dragRaf) cancelAnimationFrame(dragRaf);
      dragRaf = 0;
    };

    const onPointerDown = (e) => {
      if (!hasProgress) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      dragging = true;
      pid = e.pointerId;

      // refresh & metrics
      refreshCards();
      computeMaxV();
      computeDragMetrics();

      try {
        track.setPointerCapture(pid);
      } catch (err) {}
      e.preventDefault();
      scheduleSeek(e.clientX);
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      if (pid != null && e.pointerId !== pid) return;
      e.preventDefault();
      scheduleSeek(e.clientX);
    };

    if (hasProgress) {
      track.style.touchAction = 'none';
      bar.style.touchAction = 'none';

      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', endDrag);
    }

    /* ---- Resize handling: RO (primary), window resize (section-level fallback) ---- */
    const onResizeRO = rafOnce(() => {
      // invalidate caches
      cardsReady = false;
      maxReady = false;

      if (dragging) computeDragMetrics();
      // refresh and sync
      refreshCards();
      computeMaxV();
      syncUI();
    });

    const ro = new ResizeObserver(onResizeRO);
    ro.observe(scroller);
    if (hasProgress) ro.observe(track);

    // initial sync
    refreshCards();
    computeMaxV();
    syncUI();

    return {
      refresh: () => {
        refreshCards();
        computeMaxV();
        syncUI();
      },
      destroy: () => {
        scroller.removeEventListener('scroll', onScroll);

        if (hasNav) {
          btnPrev.removeEventListener('click', onPrev);
          btnNext.removeEventListener('click', onNext);
        }

        if (hasProgress) {
          track.removeEventListener('pointerdown', onPointerDown);
          track.removeEventListener('pointermove', onPointerMove);
          track.removeEventListener('pointerup', endDrag);
          track.removeEventListener('pointercancel', endDrag);
          track.removeEventListener('lostpointercapture', endDrag);

          if (dragRaf) cancelAnimationFrame(dragRaf);
          dragRaf = 0;
        }

        ro.disconnect();
      },
    };
  }

  /* -----------------------------
   * Tabs (per section)
   * ----------------------------- */
  function setupTabs(section, refreshActivePanel) {
    const tabs = qsa(section, '[data-ps-tab]');
    const panels = qsa(section, '[data-ps-panel]');
    if (!tabs.length || !panels.length) return;

    const activate = (id) => {
      if (!id) return;

      tabs.forEach((btn) => {
        const on = btn.getAttribute('data-ps-target') === id;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
        btn.setAttribute('tabindex', on ? '0' : '-1');
      });

      panels.forEach((p) => {
        const on = p.id === id;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      if (refreshActivePanel) requestAnimationFrame(refreshActivePanel);
    };

    // Ensure first is active if none
    if (!tabs.some((t) => t.classList.contains('is-active'))) {
      tabs[0].classList.add('is-active');
      tabs[0].setAttribute('aria-selected', 'true');
      tabs[0].setAttribute('tabindex', '0');
    }

    tabs.forEach((btn, i) => {
      const on = btn.classList.contains('is-active') || i === 0;
      btn.setAttribute('tabindex', on ? '0' : '-1');
      if (on && btn.getAttribute('aria-selected') !== 'true') btn.setAttribute('aria-selected', 'true');

      btn.addEventListener('click', () => activate(btn.getAttribute('data-ps-target')));
    });

    section.addEventListener('keydown', (e) => {
      const current = e.target.closest('[data-ps-tab]');
      if (!current) return;

      const idx = tabs.indexOf(current);
      if (idx < 0) return;

      let nextIdx = idx;
      if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') nextIdx = 0;
      else if (e.key === 'End') nextIdx = tabs.length - 1;
      else return;

      e.preventDefault();
      tabs[nextIdx].focus();
      activate(tabs[nextIdx].getAttribute('data-ps-target'));
    });
  }

  /* -----------------------------
   * Section init / cleanup
   * ----------------------------- */
  function initSection(section) {
    if (!section || INITTED.get(section)) return;
    INITTED.set(section, true);

    const panels = qsa(section, '[data-ps-panel]');
    const controllers = new Map();

    panels.forEach((p) => {
      const c = setupPanel(p);
      if (c) controllers.set(p, c);
    });

    const refreshActivePanel = () => {
      const active = qs(section, '.ps__panel.is-active[data-ps-panel]');
      const c = active ? controllers.get(active) : null;
      if (c) c.refresh();
    };

    setupTabs(section, refreshActivePanel);

    // Fallback resize (cheap, single per section)
    const onResize = rafOnce(() => controllers.forEach((c) => c.refresh()));
    window.addEventListener('resize', onResize);

    const onUnload = () => {
      window.removeEventListener('resize', onResize);
      controllers.forEach((c) => c.destroy());
      controllers.clear();
      INITTED.delete(section);
      section.removeEventListener('shopify:section:unload', onUnload);
    };

    section.addEventListener('shopify:section:unload', onUnload);

    refreshActivePanel();
  }

  const findSectionRoot = (target) => {
    if (!target) return null;
    if (target.matches && target.matches('[data-ps]')) return target;
    return target.querySelector ? target.querySelector('[data-ps]') : null;
  };

  let sectionObserver = null;
  const canObserveSection = 'IntersectionObserver' in window;

  const observeSection = (section) => {
    if (!section) return;
    if (!canObserveSection) {
      initSection(section);
      return;
    }

    if (!sectionObserver) {
      sectionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            initSection(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { root: null, rootMargin: '280px 0px' }
      );
    }

    if (!INITTED.get(section)) sectionObserver.observe(section);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('[data-ps]').forEach(observeSection);
    });
  } else {
    document.querySelectorAll('[data-ps]').forEach(observeSection);
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = findSectionRoot(e.target);
    if (section) observeSection(section);
  });
})();
